import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Pencil, Printer, X } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

// Debit Note — raised when material received against a PO is rejected. Linked to
// the respective Purchase Order for tracking/reference of rejected materials.
const blankItem = () => ({ sku: '', name: '', quantity: 0, unit_price: 0, gst_rate: 18, rejection_reason: '', batch_number: '' });

const DebitNotes = () => {
  const [debitNotes, setDebitNotes] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = { po_id: '', vendor_id: '', notes: '', items: [blankItem()] };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dnRes, poRes, vRes] = await Promise.all([
        api.get('/purchase/debit-notes'),
        api.get('/purchase/orders'),
        api.get('/purchase/vendors'),
      ]);
      setDebitNotes(dnRes.data); setPurchaseOrders(poRes.data); setVendors(vRes.data);
    } catch (error) {
      console.error('Failed to fetch debit notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm, items: [blankItem()] }); setShowModal(true); };

  const openEdit = (dn) => {
    setEditing(dn);
    setFormData({
      po_id: dn.po_id || '', vendor_id: dn.vendor_id || '', notes: dn.notes || '',
      items: (dn.items || []).map(it => ({
        sku: it.sku, name: it.name, quantity: it.quantity, unit_price: it.unit_price,
        gst_rate: it.gst_rate, rejection_reason: it.rejection_reason || '', batch_number: it.batch_number || ''
      }))
    });
    setShowModal(true);
  };

  // On selecting a PO, load its lines (only lines that had rejections are relevant,
  // but we preload all so the user can enter the rejected quantity per line).
  const onPoChange = (poId) => {
    const po = purchaseOrders.find(p => p.id === poId);
    if (!po) { setFormData(prev => ({ ...prev, po_id: '' })); return; }
    setFormData(prev => ({
      ...prev, po_id: poId, vendor_id: po.vendor_id || prev.vendor_id,
      items: (po.items || []).map(it => ({
        sku: it.sku, name: it.name, quantity: it.rejected_quantity || 0,
        unit_price: it.unit_price, gst_rate: it.gst_rate, rejection_reason: '', batch_number: it.batch_number || ''
      }))
    }));
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, blankItem()] }));
  const removeItem = (idx) => setFormData(prev => prev.items.length > 1 ? ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }) : prev);
  const updateItem = (idx, field, val) => {
    setFormData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      return { ...prev, items };
    });
  };

  const computeItem = (item) => {
    const taxable = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    const gst = (taxable * (parseFloat(item.gst_rate) || 0)) / 100;
    return {
      ...item,
      quantity: parseInt(item.quantity) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
      gst_rate: parseFloat(item.gst_rate) || 0,
      taxable_amount: taxable, cgst: gst / 2, sgst: gst / 2, igst: 0, total_amount: taxable + gst,
      rejection_reason: item.rejection_reason || '-',
    };
  };

  const formTotal = formData.items.reduce((s, it) => s + computeItem(it).total_amount, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rejItems = formData.items.filter(it => (parseInt(it.quantity) || 0) > 0);
    if (rejItems.length === 0) { toast.error('Enter at least one rejected item with a quantity'); return; }
    const payload = { items: rejItems.map(computeItem), notes: formData.notes };
    try {
      if (editing) {
        await api.put(`/purchase/debit-notes/${editing.id}`, payload);
        toast.success('Debit note updated');
      } else {
        if (!formData.po_id && !formData.vendor_id) { toast.error('Select a Purchase Order'); return; }
        await api.post('/purchase/debit-notes', { ...payload, po_id: formData.po_id, vendor_id: formData.vendor_id });
        toast.success('Debit note raised');
      }
      setShowModal(false); setEditing(null); setFormData({ ...emptyForm }); fetchAll();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to modify debit notes' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const printDN = (dn) => {
    const rows = (dn.items || []).map(it => `
      <tr>
        <td>${it.sku || ''}</td><td>${it.name || ''}</td><td>${it.batch_number || '-'}</td>
        <td style="text-align:right">${it.quantity ?? ''}</td>
        <td style="text-align:right">${inr(it.unit_price)}</td>
        <td>${it.rejection_reason || '-'}</td>
        <td style="text-align:right">${inr(it.total_amount)}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${dn.debit_note_number}</title>
      <style>
        @page{size:A4;margin:14mm}
        body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:16px}
        h1{font-size:18px;margin:0 0 4px}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin:12px 0}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #666;padding:5px 6px;text-align:left;font-size:11px}
        th{background:#1a237e;color:#fff}
        .totals{margin-top:10px;text-align:right}
        @media print{button{display:none !important}}
      </style></head><body>
      <h1>Debit Note</h1>
      <div class="meta">
        <div><strong>DN No:</strong> ${dn.debit_note_number}</div>
        <div><strong>Date:</strong> ${dn.created_at ? new Date(dn.created_at).toLocaleDateString('en-GB') : '-'}</div>
        <div><strong>Vendor:</strong> ${dn.vendor_name || '-'}</div>
        <div><strong>GSTIN:</strong> ${dn.vendor_gstin || '-'}</div>
        <div><strong>PO No:</strong> ${dn.po_number || '-'}</div>
        <div><strong>MRN No:</strong> ${dn.mrn_number || '-'}</div>
      </div>
      <table><thead><tr>
        <th>SKU</th><th>Name</th><th>Batch #</th><th>Rejected Qty</th><th>Rate</th><th>Rejection Reason</th><th>Amount</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div class="totals">
        <div>Sub Total: ${inr(dn.subtotal)}</div>
        <div>CGST: ${inr(dn.cgst_total)} &nbsp; SGST: ${inr(dn.sgst_total)} &nbsp; IGST: ${inr(dn.igst_total)}</div>
        <div style="font-weight:700;font-size:14px">Total: ${inr(dn.total_amount)}</div>
      </div>
      ${dn.notes ? `<div style="margin-top:10px"><strong>Notes:</strong> ${dn.notes}</div>` : ''}
      <button onclick="window.print()" style="margin-top:20px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="debit-notes-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Debit Notes</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="raise-dn-btn"><Plus size={16} /> Raise Debit Note</button>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading debit notes...</div>
        ) : debitNotes.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No debit notes found. Raise one when material received against a PO is rejected.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>DN #</th>
                  <th>Vendor</th>
                  <th>PO #</th>
                  <th>Items</th>
                  <th className="text-right">Subtotal</th>
                  <th className="text-right">CGST</th>
                  <th className="text-right">SGST</th>
                  <th className="text-right">Total</th>
                  <th>Created</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {debitNotes.map((dn) => (
                  <React.Fragment key={dn.id}>
                    <tr data-testid={`dn-row-${dn.debit_note_number}`} style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === dn.id ? null : dn.id)}>
                      <td>{expanded === dn.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="font-mono">{dn.debit_note_number}</td>
                      <td>{dn.vendor_name}</td>
                      <td className="font-mono text-sm">{dn.po_number || '-'}</td>
                      <td>{dn.items?.length || 0} items</td>
                      <td className="text-right tabular-nums">{inr(dn.subtotal)}</td>
                      <td className="text-right tabular-nums">{inr(dn.cgst_total)}</td>
                      <td className="text-right tabular-nums">{inr(dn.sgst_total)}</td>
                      <td className="text-right tabular-nums font-semibold">{inr(dn.total_amount)}</td>
                      <td className="text-sm">{new Date(dn.created_at).toLocaleDateString()}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="flex gap-1" style={{ justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => openEdit(dn)} className="btn btn-ghost btn-sm" title="Edit"><Pencil size={14} /></button>
                          <button onClick={() => printDN(dn)} className="btn btn-ghost btn-sm" title="Print"><Printer size={14} /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded === dn.id && (
                      <tr data-testid={`dn-detail-${dn.debit_note_number}`}>
                        <td colSpan={11} style={{ background: 'var(--bg-secondary, #f8fafc)', padding: '12px 16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Rejected items — batch &amp; reason</div>
                          <table style={{ width: '100%', fontSize: '12px' }}>
                            <thead>
                              <tr>
                                <th style={{ padding: '4px', textAlign: 'left' }}>SKU</th>
                                <th style={{ padding: '4px', textAlign: 'left' }}>Name</th>
                                <th style={{ padding: '4px', textAlign: 'left' }}>Batch #</th>
                                <th style={{ padding: '4px', textAlign: 'right' }}>Rejected Qty</th>
                                <th style={{ padding: '4px', textAlign: 'left' }}>Rejection Reason</th>
                                <th style={{ padding: '4px', textAlign: 'right' }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(dn.items || []).map((it, i) => (
                                <tr key={i}>
                                  <td style={{ padding: '4px' }} className="font-mono">{it.sku}</td>
                                  <td style={{ padding: '4px' }}>{it.name}</td>
                                  <td style={{ padding: '4px' }} className="font-mono">{it.batch_number || '-'}</td>
                                  <td style={{ padding: '4px', textAlign: 'right', color: '#DC2626' }} className="tabular-nums">{it.quantity}</td>
                                  <td style={{ padding: '4px' }}>{it.rejection_reason || '-'}</td>
                                  <td style={{ padding: '4px', textAlign: 'right' }} className="tabular-nums">{inr(it.total_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="dn-modal">
          <div className="modal-content" style={{ maxWidth: '860px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit ${editing.debit_note_number}` : 'Raise Debit Note'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editing && (
                  <div className="form-group">
                    <label className="form-label">Purchase Order *</label>
                    <select className="form-select" value={formData.po_id} onChange={(e) => onPoChange(e.target.value)} required data-testid="dn-po">
                      <option value="">Select PO (rejected materials)...</option>
                      {purchaseOrders.map(po => <option key={po.id} value={po.id}>{po.po_number} - {po.vendor_name}</option>)}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>Rejected quantities auto-load from the PO; adjust and add the rejection reason per line.</div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '12px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Rejected Items</h3>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Item</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr>
                    <th style={{ padding: 6 }}>SKU</th><th style={{ padding: 6 }}>Name</th>
                    <th style={{ padding: 6 }}>Batch #</th><th style={{ padding: 6 }}>Rej. Qty</th>
                    <th style={{ padding: 6 }}>Rate</th><th style={{ padding: 6 }}>GST%</th>
                    <th style={{ padding: 6 }}>Rejection Reason</th>
                    <th style={{ padding: 6, textAlign: 'right' }}>Amount</th><th style={{ width: 32 }}></th>
                  </tr></thead>
                  <tbody>
                    {formData.items.map((item, idx) => {
                      const c = computeItem(item);
                      return (
                        <tr key={idx}>
                          <td style={{ padding: 4 }}><input type="text" className="form-input" value={item.sku} onChange={(e) => updateItem(idx, 'sku', e.target.value)} style={{ fontSize: 12, padding: 4, width: 90 }} /></td>
                          <td style={{ padding: 4 }}><input type="text" className="form-input" value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} style={{ fontSize: 12, padding: 4, minWidth: 110 }} /></td>
                          <td style={{ padding: 4 }}><input type="text" className="form-input" value={item.batch_number} onChange={(e) => updateItem(idx, 'batch_number', e.target.value)} style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                          <td style={{ padding: 4 }}><input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                          <td style={{ padding: 4 }}><input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                          <td style={{ padding: 4 }}><input type="number" className="form-input" value={item.gst_rate} onChange={(e) => updateItem(idx, 'gst_rate', e.target.value)} style={{ fontSize: 12, padding: 4, width: 50 }} /></td>
                          <td style={{ padding: 4 }}><input type="text" className="form-input" value={item.rejection_reason} onChange={(e) => updateItem(idx, 'rejection_reason', e.target.value)} style={{ fontSize: 12, padding: 4, minWidth: 120 }} /></td>
                          <td style={{ padding: 4, textAlign: 'right' }} className="tabular-nums">{inr(c.total_amount)}</td>
                          <td style={{ padding: 4 }}>{formData.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 700 }}>Total: {inr(formTotal)}</div>

                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-dn-btn">{editing ? 'Update' : 'Raise Debit Note'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebitNotes;
